// Blake's Help guides (Joe, 4 Nov 2026).
//
// Short, task-based guides written from how Blake works now. Button and tab
// names are written exactly as they appear on screen, inside [[ ]] — the Help
// page shows those as buttons so people can spot them. `pages` are the screens
// a guide belongs to: the "?" in the header opens Help on the guides for the
// page you're on.
//
// When a screen changes, change its guide here too.

export const AUDIENCES = [
  { key: 'warehouse', label: 'Warehouse & production' },
  { key: 'office', label: 'Office & accounts' },
];

export const GUIDES = [
  // ── Warehouse & production ────────────────────────────────────────────────
  {
    id: 'receive-delivery',
    audience: 'warehouse',
    title: 'Receive a delivery from a supplier',
    pages: ['/stock'],
    summary: 'Book in goods that arrive against a purchase order, so the stock, its batch and its location are right.',
    steps: [
      'Go to Stock and open the [[Purchase Orders]] tab.',
      'Open the purchase order the delivery is for (search by supplier or PO number).',
      'For one line, press [[Receive]] next to it. For everything at once, press [[Receive All (N)]].',
      "Enter the quantity that actually came, where you've put it (Location) and the supplier's lot / batch number. Part deliveries are fine — the rest stays outstanding.",
      'Press [[Confirm Receipt]]. The stock goes on and the PO shows as part received or received.',
      'Press [[📋 Goods Receipt]] to print the receipt for the file.',
      "If the supplier's invoice or pro-forma came with it, press [[Upload Pro-forma / Invoice]] on the PO to keep it with the order.",
    ],
    tips: [
      "Something came that isn't on a PO? Raise the PO first with [[+ New PO]], then receive against it.",
      'Received the wrong amount? Fix it on the stock item with [[Adjust Stock]] and give a reason.',
    ],
  },
  {
    id: 'client-intake',
    audience: 'warehouse',
    title: "Book in a client's stock",
    pages: ['/clients'],
    summary: 'When a contract client sends us spirit, packaging or ingredients to use or store.',
    steps: [
      'Go to Contract Clients and open the client.',
      'Open the [[Intakes]] tab and press [[+ New Intake]]. Enter what is expected.',
      'When it arrives, press [[✓ Check In]], check the quantities and batches and say where it has gone.',
      "If the client filled in our goods intake form online, it waits on the [[Goods Intake]] tab at the top of Contract Clients. Open it, match each line to a stock item (or create one), and press [[Approve & Create Draft PO]]. Receive against that PO when it arrives.",
    ],
    tips: ["The client's own goods intake link is on their [[Intakes]] tab under Goods Intake Link — send them that rather than emailing forms."],
  },
  {
    id: 'pallets',
    audience: 'warehouse',
    title: 'Put stock on a pallet, move it, take it off',
    pages: ['/storage-units', '/warehouse'],
    summary: 'Pallets, IBCs and other storage units: what is on them and where they are.',
    steps: [
      'Go to Storage Units. To start a new pallet press [[+ New Unit]], choose the type and where it is.',
      'Open the unit and press [[+ Put stock on]]. Pick the item and batch — it comes off wherever that stock is now.',
      'To move the whole pallet, press [[Move]] and choose the new location.',
      'To take some stock off, press [[Take off]] on that line and say how much and where it goes.',
      'Press [[Preview label]] to print the pallet label. To print several, tick them in the list and press [[Preview labels]].',
    ],
    tips: ['Warehouse Map shows where every unit is. Use [[Change owner]] when stock on a pallet belongs to a different client.'],
  },
  {
    id: 'consignments',
    audience: 'warehouse',
    title: 'Send pallets out as a consignment',
    pages: ['/storage-units'],
    summary: 'A load of whole pallets leaving together — a bulk transfer, export or delivery not picked as a despatch run.',
    steps: [
      'Go to Storage Units and open the [[Consignments]] tab. Press [[+ New consignment]] and fill in who it is for and how it is going.',
      'Press [[+ Add pallets]] and tick the pallets going. (Or tick them in the Units list and press [[Add to consignment…]].)',
      'Press [[Preview manifest]] to print the manifest for the driver.',
      'When it has gone, press [[Mark dispatched]]. If the spirit is in bond, Blake asks how it is leaving (duty paid, transfer in bond or export) so the duty and the W1 are right.',
    ],
    tips: [
      'Marking a consignment dispatched takes the stock off the pallets. If the consignment is linked to an order, the order is despatched too.',
      'Dispatched by mistake? Reopen the consignment — the stock goes back on the pallets.',
    ],
  },
  {
    id: 'despatch-run',
    audience: 'warehouse',
    title: 'Pick, pack and send a despatch run',
    pages: ['/orders'],
    summary: 'Confirmed orders are picked and packed together in a run, then marked despatched in one go.',
    steps: [
      'Go to Orders. Orders waiting to go are on [[Ready to pick]].',
      'Tick the orders going today and press [[Start despatch run]]. (If a run is already open, [[Add to run N]] adds them to it.)',
      'In the run, print the [[Picking list]] and pick everything on it.',
      'Print the [[Packing slips]] and put the right slip in each parcel.',
      'Tick [[Packed]] against each order as it is done.',
      'For DPD parcels, press [[Check & download]] on the DPD file and upload it to DPD.',
      'When everything is packed, press [[Mark run despatched (N)]]. Stock comes off and Shopify customers get their despatch email.',
    ],
    tips: [
      "An order can't go today? Press [[Take out of run]] and it goes back to Ready to pick.",
      '[[Cancel run]] puts every order back on Ready to pick.',
    ],
  },
  {
    id: 'production-run',
    audience: 'warehouse',
    title: 'Run a production batch',
    pages: ['/production', '/production-schedule'],
    summary: 'Record what goes into a run and what comes out, so stock, batches and the W1 follow.',
    steps: [
      'Go to Production and press [[+ New Run]].',
      'Choose Own Brand or Contract, the recipe type, the recipe and the target volume. Leave "Operates in bond" ticked for spirit under bond. Press [[Create Run]].',
      'Open the run. Each ingredient from the recipe is listed — press [[Take off stock]] for each one as you use it, and change the amount or batch if it was different.',
      'When the batch is made, press [[Record what came out]] and enter the volume and ABV.',
      'Press [[Finish the run]] (or [[Finish …]] for each stage of a multi-stage run).',
      'Add the checks with [[+ Add QC Record]], then [[✓ Sign off now]] — or [[✎ Request Sign-off]] if someone else signs it off.',
    ],
    tips: [
      'Made a mistake? [[Reopen this run]], or [[Cancel run and give everything back]] to put the stock back.',
      'The Production Schedule shows what is planned for each day.',
    ],
  },
  {
    id: 'bottling',
    audience: 'warehouse',
    title: 'Bottle a batch',
    pages: ['/production'],
    summary: 'A bottling run is a production run with a Packaging recipe.',
    steps: [
      'Press [[+ New Run]] on Production and choose Packaging as the recipe type, then the product.',
      'Take off the liquid and the dry goods (bottles, closures, labels, cases) with [[Take off stock]].',
      'Press [[Give it a bottle batch number]] — that is the number on the bottles and cases.',
      'Press [[Record what came out]] with the number of bottles, then [[Finish the run]].',
      'Press [[Case labels]] and then [[Print N]] to print the case labels.',
    ],
  },
  {
    id: 'stock-take',
    audience: 'warehouse',
    title: 'Do a stock take',
    pages: ['/stock-take'],
    summary: 'Count what is really there and put Blake right.',
    steps: [
      'Go to Stock Take and press [[+ New Stock Take]]. Choose Full (everything) or Rolling (a section), then [[Start Stock Take]].',
      'For each item press [[+ Add Count]] and enter what you counted — or [[📷 Scan Pallet / Batch]] to scan a label.',
      'When counting is done, press [[Calculate Variances]].',
      'Anything that looks wrong: [[Recount]]. When a count is right: [[Set as stock]].',
      'A manager finishes with [[Approve & Sign Off]].',
    ],
  },
  {
    id: 'stock-adjust',
    audience: 'warehouse',
    title: 'Correct a stock figure or print a label',
    pages: ['/stock'],
    summary: 'For one-off corrections between stock takes.',
    steps: [
      'Go to Stock, [[Stock Items]] tab, and open the item.',
      'Press [[Adjust Stock]], enter the change (or the new figure) and pick a reason. Breakages and samples need a reason so they show on reports.',
      'Press [[Print Material Label]] for a label for that material.',
    ],
  },

  // ── Office & accounts ─────────────────────────────────────────────────────
  {
    id: 'orders-email',
    audience: 'office',
    title: 'Orders that come in by email',
    pages: ['/orders'],
    summary: "Blake reads the orders inbox and turns clients' order files into draft orders.",
    steps: [
      'Go to Orders, [[Email inbox]] tab. New emails come in on their own — press [[Check inbox now]] to fetch straight away.',
      'Press [[Review]] on an email. If it is an order file from a fulfilment client, press [[Import files]].',
      'Open the [[Order files]] tab. Files waiting are on [[To review]].',
      'Any line Blake could not match shows [[Match product…]] — pick the right product once and Blake remembers it.',
      'Press [[Create N draft orders]]. The orders appear on [[To confirm]] in the Orders tab.',
      'Not an order (a statement, a newsletter)? Press [[Not an order]].',
    ],
    tips: [
      'If Blake read an email badly, [[Read again (with attachments)]] tries again.',
      "[[Senders]] tells Blake which client an email address belongs to and what to do with their emails. Use it when a client's emails aren't being picked up.",
      '[[Original file]] opens the file exactly as the client sent it.',
    ],
  },
  {
    id: 'orders-new',
    audience: 'office',
    title: 'Enter and confirm an order',
    pages: ['/orders'],
    summary: 'Trade (sales) orders and fulfilment orders entered by hand.',
    steps: [
      'Go to Orders and press [[+ New sales order]] (we sell it) or [[+ New fulfilment order]] (we send a client\'s stock for them).',
      "Choose the customer, add the products and enter the customer's purchase order number if they gave one — it is printed on the paperwork and the invoice.",
      'The order goes to [[To confirm]]. Tick it and press [[Confirm]] — that reserves the stock and moves it to [[Ready to pick]].',
      "To add or change the customer's PO number later, open the order and use the PO field at the top.",
    ],
    tips: ['Several orders at once: tick them all on To confirm and press [[Confirm]] once.'],
  },
  {
    id: 'despatch-note',
    audience: 'office',
    title: 'Print or save a despatch note',
    pages: ['/orders'],
    summary: 'The despatch note goes with the goods and is signed on collection or delivery.',
    steps: [
      'Open the order.',
      'If it is going in one of our vans, pick the van in [[Which van?]] — it is ticked on the note.',
      'Press [[📄 Despatch note]] to open it (print, or save as PDF), or [[Print despatch note]] to send it to the printer.',
    ],
    tips: [
      'How it goes (DPD, collection, our van) is filled in from the order.',
      'Vans and the AWRS number are set in Settings → [[🏢 Company]].',
    ],
  },
  {
    id: 'shopify',
    audience: 'office',
    title: 'Shopify orders and stock',
    pages: ['/orders', '/settings'],
    summary: 'Shopify orders arrive in Blake by themselves; Blake tells Shopify when they go and how much stock there is.',
    steps: [
      'Shopify orders appear on Orders without anyone doing anything. Pick and pack them as normal.',
      'When the run is marked despatched, Shopify marks the order fulfilled and emails the customer.',
      'Shopify stock follows what Blake has available every hour. To push it straight away, go to Settings → [[🔌 Integrations]] → Shopify and press [[Sync now]].',
      "If a Shopify fulfilment didn't go through, it's listed in the same place to try again.",
    ],
    tips: ['Products are matched on SKU, so the SKU in Shopify must match the Blake SKU.'],
  },
  {
    id: 'billing',
    audience: 'office',
    title: 'Bill clients and send invoices to Xero',
    pages: ['/billing'],
    summary: 'Everything chargeable — trade orders, fulfilment, storage and duty re-charges — waits on Billing until it is invoiced.',
    steps: [
      'Go to Billing. [[To invoice]] lists everything waiting; filter by Trade orders, Fulfilment, Storage or Duty re-charges.',
      'Check the line and press [[Send to Xero]]. It goes to Xero as a draft invoice with the right branding theme, products and quantities (parcels for fulfilment, LPA for duty).',
      'To send several, tick them and press [[Send selected to Xero]].',
      'Invoiced it some other way? Press [[Mark invoiced]] (or [[Mark selected invoiced]]).',
      'Approve and send the invoice from Xero as usual.',
    ],
    tips: [
      'Sent lines move to [[Sent]], filed under the month they are for — not the day they were sent.',
      '[[Undo]] puts a line back on To invoice.',
      'Monthly storage shows "Month running" until the month ends.',
      '[[Statement (CSV)]] downloads a statement for a client.',
    ],
  },
  {
    id: 'xero-setup',
    audience: 'office',
    title: 'Xero: connection, themes and products',
    pages: ['/settings', '/billing'],
    summary: 'One-off set-up, and what to do if Xero stops accepting invoices.',
    steps: [
      'Go to Settings → [[🔌 Integrations]] → Xero.',
      'If Xero says Blake isn\'t authorised, press [[Reconnect]] and log in to Xero again.',
      'Under Branding themes, choose the theme for trade invoices and the one for contract invoices.',
      'Press [[Check products]] to compare Xero\'s products with Blake\'s SKUs, then [[Apply in Xero]] to recode them.',
      'Account codes for each charge (e.g. duty) are under [[💷 Rates & Billing]]. If the accountants change a code, change it there.',
    ],
  },
  {
    id: 'rates',
    audience: 'office',
    title: 'Change a fulfilment or storage rate',
    pages: ['/settings', '/billing'],
    summary: 'What each client pays per order, parcel or pallet.',
    steps: [
      'Go to Settings → [[💷 Rates & Billing]].',
      'Find the rate and press [[Edit]]. Change the figures.',
      'Blake shows which orders have not been billed yet — choose whether they take the new rate too.',
    ],
  },
  {
    id: 'w1',
    audience: 'office',
    title: 'Do the W1 return',
    pages: ['/hmrc'],
    summary: 'The monthly warehouse return for HMRC, worked out from Blake\'s movements.',
    steps: [
      'Go to HMRC, [[W1 Periods]] tab, and press [[+ Calculate Period]] for the month.',
      'Open the period and check each box against what you know. If you fix anything in Blake, press [[Recalculate]].',
      'When it is right, press [[Lock Period]] and enter your 2FA code. It can\'t change after that.',
      'Submit it to HMRC, then press [[Mark as Submitted]].',
      'When HMRC acknowledge it, press [[Record Acknowledgement]] and enter their reference.',
      'Press [[↓ PDF]] to keep a copy.',
    ],
    tips: ['Locking needs 2FA set up — Settings → [[🔐 My 2FA]].'],
  },
  {
    id: 'duty',
    audience: 'office',
    title: 'Duty: own brand and client re-charges',
    pages: ['/hmrc', '/billing'],
    summary: 'Duty on spirit leaving bond — what we owe HMRC and what clients owe us.',
    steps: [
      'Every duty-paid release is on HMRC → [[Duty Movements]].',
      'Own Brand duty shows as "Owed to HMRC" until it is paid.',
      'When the month\'s duty is paid, go to HMRC → [[APPA]], open the month and press [[Mark duty paid to HMRC]]. Its own-brand movements show as paid.',
      'Duty for clients\' spirit is re-charged to them through Billing → [[To invoice]] (Duty re-charges).',
    ],
    tips: ['Pressed it by mistake? There is an [[Undo]] next to it. Duty rates are on the [[Duty Rates]] tab.'],
  },
  {
    id: 'new-client',
    audience: 'office',
    title: 'Set up a new client',
    pages: ['/clients'],
    summary: 'A new contract or fulfilment client, their contacts and their agreement.',
    steps: [
      'Go to Contract Clients and press [[+ New Client]].',
      'Or, if they filled in our customer form, press [[Import customer form]] and choose the file — Blake fills in the details and contacts. [[Download blank form]] gets a fresh one to send them.',
      'Check the [[Details]] and [[Contacts]] tabs. Mark who is the accounts contact.',
      'On [[Agreements]], start the right agreement (development, bulk or consulting), or press [[Upload agreement]] for one signed elsewhere.',
      'Press [[Sign & send]] to sign it for PLD and email it to them.',
    ],
    tips: ['Recipes are never shared with clients — keep them out of anything sent to a client.'],
  },
  {
    id: 'new-supplier',
    audience: 'office',
    title: 'Add a supplier, and fill in their account form',
    pages: ['/stock'],
    summary: 'Suppliers live on the Stock page.',
    steps: [
      'Go to Stock, [[Suppliers]] tab, and press [[+ New Supplier]].',
      'If the supplier sent an account application form, press [[Account form]] on the supplier and upload it — Blake fills it in with our details for you to check and send back.',
    ],
  },
  {
    id: 'purchase-order',
    audience: 'office',
    title: 'Raise and send a purchase order',
    pages: ['/stock'],
    summary: 'Order materials from a supplier.',
    steps: [
      'Go to Stock, [[Purchase Orders]] tab, and press [[+ New PO]] — or [[📄 PO from quote]] to build it from the supplier\'s quote.',
      'Check the lines and prices, then press [[✉ Send to Supplier]].',
      'When it arrives, receive it — see "Receive a delivery from a supplier".',
    ],
  },
  {
    id: 'new-stock-item',
    audience: 'office',
    title: 'Add a new stock item without duplicating one',
    pages: ['/stock', '/recipe-dev'],
    summary: 'New materials, ingredients and products.',
    steps: [
      'Go to Stock and press [[+ New Item]]. As you type the name Blake shows items that look similar — use one of those if it is the same thing.',
      'If Blake says it is already in stock but it really is different, tick "It really is a different item — add it anyway".',
      'In Recipe Development you can add a new ingredient without leaving the recipe: in the ingredient picker choose "+ Add … to stock".',
    ],
  },
  {
    id: 'product-spec',
    audience: 'office',
    title: 'Write a product specification or safety data sheet',
    pages: ['/product-specs'],
    summary: 'Specs and SDS documents for customers.',
    steps: [
      'Go to Product Specs and press [[+ New Spec]].',
      'Choose the recipe at the top and press [[Fill in from recipe]]. Tick what to bring in — ingredients, allergens, ABV and so on.',
      'Fill in the rest. Anything left blank uses the standard wording shown in grey.',
      'Save, then open the spec and press [[Safety data sheet]] for the SDS.',
    ],
    tips: ['The spec lists ingredients, not the recipe — quantities never go on it.'],
  },
  {
    id: 'recipe-dev',
    audience: 'office',
    title: 'Develop a recipe for a client',
    pages: ['/recipe-dev', '/recipes'],
    summary: 'From first samples to a production recipe.',
    steps: [
      'Go to Recipe Development and start a [[New project]] for the client.',
      'Add a round for each set of samples, with the ingredients ([[+ Add ingredient]]) and the client\'s verdict.',
      'When a version is approved, press [[Make recipe]]. It becomes a draft on the Recipes page, ready to check and make active.',
    ],
  },
];

/** Guides for a page (the "?" in the header). */
export function guidesForPath(path) {
  if (!path || path === '/') return [];
  return GUIDES.filter((g) => g.pages.some((p) => path === p || path.startsWith(`${p}/`)));
}
